/**
 * Space Settings Dialog
 *
 * Edits Space metadata and related management surfaces.
 *
 * Primary responsibilities:
 * - Update Space details and preferences
 * - Edit custom instructions (same field as Create Space → Context)
 * - Expose Space memory management
 * - Handle archive and delete actions
 *
 * Dependencies:
 * - db/spaces
 * - SpaceMemoriesManager
 * - SpaceCustomInstructionsEditor
 */

"use client"

import { FC, useState, useEffect } from "react"
import { Tables } from "@/supabase/types"
import {
  IconSettings,
  IconArchive,
  IconTrash,
  IconX
} from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog"
import { updateSpace, archiveSpace, deleteSpace } from "@/db/spaces"
import { toast } from "sonner"
import { SpaceMemoriesManager } from "./space-memories-manager"
import { SpaceCustomInstructionsEditor } from "./space-standing-instructions-editor"
import { useSplitPaneNavigation } from "@/components/splitview/split-pane-context"
import {
  type SpaceViewType,
  normalizeSpaceViewType
} from "@/types/spaces"
import { normalizeSpaceDisplayPreferences } from "@/lib/spaces/display-preferences"

interface SpaceSettingsDialogProps {
  space: Tables<"spaces">
  isOpen: boolean
  onClose: () => void
  onUpdate: (space?: Tables<"spaces">) => void
  onDeleted?: (spaceId: string) => void
}

export const SpaceSettingsDialog: FC<SpaceSettingsDialogProps> = ({
  space,
  isOpen,
  onClose,
  onUpdate,
  onDeleted
}) => {
  const { navigateToHref } = useSplitPaneNavigation()
  const [name, setName] = useState(space.name || "")
  const [description, setDescription] = useState(space.description || "")
  const [customInstructions, setCustomInstructions] = useState(
    space.custom_instructions || ""
  )
  const [memoryIsolation, setMemoryIsolation] = useState(
    space.memory_isolation === "space_only" ? "space_only" : "default"
  )
  const [icon, setIcon] = useState(space.icon || "")
  const [color, setColor] = useState(space.color || "")
  const [defaultView, setDefaultView] = useState<SpaceViewType>(
    normalizeSpaceViewType(space.default_view)
  )
  const [showDayOverviewProgress, setShowDayOverviewProgress] = useState(false)
  const [showItemsProgressOverview, setShowItemsProgressOverview] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isArchiving, setIsArchiving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    if (isOpen && space) {
      setName(space.name || "")
      setDescription(space.description || "")
      setCustomInstructions(space.custom_instructions || "")
      setMemoryIsolation(
        space.memory_isolation === "space_only" ? "space_only" : "default"
      )
      setIcon(space.icon || "")
      setColor(space.color || "")
      setDefaultView(normalizeSpaceViewType(space.default_view))
      const displayPreferences = normalizeSpaceDisplayPreferences(
        space.display_preferences
      )
      setShowDayOverviewProgress(displayPreferences.showDayOverviewProgress)
      setShowItemsProgressOverview(displayPreferences.showItemsProgressOverview)
    }
  }, [isOpen, space])

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Space name is required")
      return
    }

    setIsSaving(true)
    try {
      const updatedSpace = await updateSpace(space.id, {
        name: name.trim(),
        description: description.trim() || null,
        custom_instructions: customInstructions.trim() || null,
        memory_isolation: memoryIsolation,
        icon: icon.trim() || null,
        color: color.trim() || null,
        default_view: normalizeSpaceViewType(defaultView),
        display_preferences: {
          showDayOverviewProgress,
          showItemsProgressOverview
        }
      })
      toast.success("Space updated successfully")
      onUpdate(updatedSpace)
      onClose()
    } catch (error) {
      console.error("Error updating space:", error)
      toast.error("Failed to update space")
    } finally {
      setIsSaving(false)
    }
  }

  const handleArchive = async () => {
    setShowArchiveConfirm(false)
    setIsArchiving(true)
    try {
      const archivedSpace = await archiveSpace(space.id)
      toast.success("Space archived")
      onUpdate(archivedSpace)
      onClose()
      navigateToHref("/spaces")
    } catch (error) {
      console.error("Error archiving space:", error)
      toast.error("Failed to archive space")
    } finally {
      setIsArchiving(false)
    }
  }

  const handleDelete = async () => {
    setShowDeleteConfirm(false)
    setIsDeleting(true)
    try {
      await deleteSpace(space.id)
      toast.success("Space deleted")
      onDeleted?.(space.id)
      onUpdate()
      onClose()
      navigateToHref("/spaces")
    } catch (error) {
      console.error("Error deleting space:", error)
      toast.error("Failed to delete space")
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-dvh overflow-y-auto p-4 sm:max-h-[85vh] sm:max-w-2xl sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconSettings size={20} />
            Space Settings
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <h3 className="mb-3 text-sm font-semibold">Basic Information</h3>
            </div>

            <div className="space-y-2">
              <Label htmlFor="space-name">Space Name *</Label>
              <Input
                id="space-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter space name"
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="space-description">Description</Label>
              <Textarea
                id="space-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter space description (optional)"
                rows={3}
                maxLength={1000}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="space-icon">Icon</Label>
                <Input
                  id="space-icon"
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                  placeholder="🎯 (emoji)"
                  maxLength={100}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="space-color">Color</Label>
                <Input
                  id="space-color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#3b82f6"
                  maxLength={20}
                />
              </div>
            </div>

            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex min-h-12 touch-manipulation items-center justify-between gap-4">
                <div className="space-y-1">
                  <Label htmlFor="day-overview-progress">
                    Day&apos;s Overview progress
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    Show a compact percent complete in the Day&apos;s Overview strip.
                  </p>
                </div>
                <Switch
                  id="day-overview-progress"
                  checked={showDayOverviewProgress}
                  onCheckedChange={setShowDayOverviewProgress}
                />
              </div>

              <div className="flex min-h-12 touch-manipulation items-center justify-between gap-4">
                <div className="space-y-1">
                  <Label htmlFor="items-progress-overview">
                    Items progress overview
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    Show progress summary cards above the items list.
                  </p>
                </div>
                <Switch
                  id="items-progress-overview"
                  checked={showItemsProgressOverview}
                  onCheckedChange={setShowItemsProgressOverview}
                />
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <div>
              <h3 className="mb-1 text-sm font-semibold">Context</h3>
              <p className="text-muted-foreground text-xs">
                Custom instructions for every chat in this Space.
              </p>
            </div>
            <SpaceCustomInstructionsEditor
              value={customInstructions}
              onChange={setCustomInstructions}
              workspaceId={space.workspace_id}
              spaceName={name}
              spaceDescription={description}
              disabled={isSaving || isArchiving || isDeleting}
            />
            <div className="flex min-h-12 touch-manipulation items-center justify-between gap-4 rounded-lg border p-3">
              <div className="space-y-1">
                <Label htmlFor="memory-isolation">
                  Use only this Space&apos;s memories in Space chats
                </Label>
                <p className="text-muted-foreground text-xs">
                  When on, personal memories stay out of this Space&apos;s chats.
                  Space and reference memories still apply.
                </p>
              </div>
              <Switch
                id="memory-isolation"
                checked={memoryIsolation === "space_only"}
                onCheckedChange={checked =>
                  setMemoryIsolation(checked ? "space_only" : "default")
                }
                disabled={isSaving || isArchiving || isDeleting}
              />
            </div>
          </div>

          <Separator />

          {/* Preferences */}
          <div className="space-y-4">
            <div>
              <h3 className="mb-3 text-sm font-semibold">Preferences</h3>
            </div>

            <div className="space-y-2">
              <Label htmlFor="default-view">Default View</Label>
              <Select
                value={defaultView}
                onValueChange={(value) =>
                  setDefaultView(normalizeSpaceViewType(value))
                }
              >
                <SelectTrigger id="default-view">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="list">List</SelectItem>
                  <SelectItem value="board">Board</SelectItem>
                  <SelectItem value="table">Table</SelectItem>
                  <SelectItem value="calendar">Calendar</SelectItem>
                  <SelectItem value="gantt">Gantt</SelectItem>
                  <SelectItem value="timeline">Timeline</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Space Memories */}
          <div className="space-y-4">
            <div>
              <h3 className="mb-3 text-sm font-semibold">Space Memories</h3>
              <p className="text-muted-foreground mb-4 text-xs">
                Manage memories that AI remembers about this space. These memories help provide context in all conversations within this space.
              </p>
            </div>
            <SpaceMemoriesManager spaceId={space.id} />
          </div>

          <Separator />

          {/* Danger Zone */}
          <div className="space-y-4">
            <div>
              <h3 className="text-destructive mb-3 text-sm font-semibold">Danger Zone</h3>
            </div>

            <div className="space-y-3">
              <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium">Archive Space</p>
                  <p className="text-muted-foreground text-xs">
                    Hide this space from your main view. You can unarchive it later.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="min-h-10 touch-manipulation"
                  onClick={() => setShowArchiveConfirm(true)}
                  disabled={isArchiving}
                >
                  <IconArchive size={16} className="mr-2" />
                  {isArchiving ? "Archiving..." : "Archive"}
                </Button>
              </div>

              <div className="border-destructive flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-destructive text-sm font-medium">Delete Space</p>
                  <p className="text-muted-foreground text-xs">
                    Permanently delete this space. This action can be undone within 30 days.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  className="min-h-10 touch-manipulation"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                >
                  <IconTrash size={16} className="mr-2" />
                  {isDeleting ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button className="min-h-10 touch-manipulation" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="min-h-10 touch-manipulation"
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

      <AlertDialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this space?</AlertDialogTitle>
            <AlertDialogDescription>
              This hides the space from your main view. You can unarchive it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                void handleArchive()
              }}
              disabled={isArchiving}
            >
              {isArchiving ? "Archiving..." : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this space?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the space and its contents. You can restore it within
              30 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                void handleDelete()
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
